export const getWorkoutItems = (w) => {
  let items = [];
  if (w.exercises) {
    w.exercises.forEach(block => {
      if (block.type === 'superset') items = items.concat(block.items);
      else items.push(block);
    });
  }
  return items;
};

export const getWorkoutSets = (w) => {
  let sets = [];
  getWorkoutItems(w).forEach(ex => {
    if (ex.sets) sets = sets.concat(ex.sets);
  });
  return sets;
};

import { calculateWorkoutVolume } from './exerciseUtils';

export const BADGES = [
  {
    id: 'beta_tester',
    name: 'Beta Testeur 🎖️',
    description: "Pionnier ayant testé l'application en avant-première (réservé aux 30 premiers inscrits)",
    category: 'special',
    isSpecial: true,
    check: (workouts, goals, profile) => {
      return Boolean(profile?.isBetaTester || (Array.isArray(profile?.badges) && profile.badges.includes('beta_tester')));
    }
  },
  {
    id: 'first_step',
    name: 'Premier Pas 👶',
    description: 'A terminé sa première séance',
    check: (workouts) => workouts.length >= 1
  },
  {
    id: '10_workouts',
    name: 'Initié 🥉',
    description: "A terminé 10 séances d'entraînement",
    check: (workouts) => workouts.length >= 10
  },
  {
    id: '50_workouts',
    name: 'Confirmé 🥈',
    description: "A terminé 50 séances d'entraînement",
    check: (workouts) => workouts.length >= 50
  },
  {
    id: '100_workouts',
    name: 'Légende 🥇',
    description: "A terminé 100 séances d'entraînement",
    check: (workouts) => workouts.length >= 100
  },
  {
    id: 'lightweight',
    name: 'Poids Plume 🪶',
    description: 'A soulevé 10 tonnes au total',
    check: (workouts) => {
      let total = 0;
      workouts.forEach(w => { total += (w.totalVolume !== undefined ? w.totalVolume : calculateWorkoutVolume(w)); });
      return total >= 10000;
    }
  },
  {
    id: '100_tonnes',
    name: 'Locomotive 🚂',
    description: 'A soulevé plus de 100 tonnes au total',
    check: (workouts) => {
      let total = 0;
      workouts.forEach(w => { total += (w.totalVolume !== undefined ? w.totalVolume : calculateWorkoutVolume(w)); });
      return total >= 100000;
    }
  },
  {
    id: 'heavyweight',
    name: 'Titan 🏔️',
    description: 'A soulevé plus de 500 tonnes au total',
    check: (workouts) => {
      let total = 0;
      workouts.forEach(w => { total += (w.totalVolume !== undefined ? w.totalVolume : calculateWorkoutVolume(w)); });
      return total >= 500000;
    },
    secret: true
  },
  {
    id: 'one_tonne_club',
    name: 'Club des 1000 🏋️',
    description: 'A soulevé plus de 1000 kg en une seule séance',
    check: (workouts) => workouts.some(w => (w.totalVolume !== undefined ? w.totalVolume : calculateWorkoutVolume(w)) >= 1000)
  },
  {
    id: 'easy_day',
    name: 'Journée Chill 🛋️',
    description: "A enregistré une séance avec un ressenti Facile",
    check: (workouts) => workouts.some(w => w.feeling === 'facile')
  },
  {
    id: 'warrior',
    name: 'Guerrier 🩸',
    description: 'A enregistré une séance avec un ressenti Extrême',
    check: (workouts) => workouts.some(w => w.feeling === 'extreme')
  },
  {
    id: 'flash',
    name: 'L\'Éclair ⚡',
    description: 'A terminé une séance en moins de 20 minutes',
    check: (workouts) => workouts.some(w => w.duration && parseInt(w.duration) > 0 && parseInt(w.duration) < 20)
  },
  {
    id: 'marathon',
    name: 'Marathonien 🏃‍♂️',
    description: 'A fait une séance de plus de 2 heures',
    check: (workouts) => workouts.some(w => w.duration && parseInt(w.duration) >= 120)
  },
  {
    id: 'regular',
    name: 'Régulier 📅',
    description: 'A fait au moins 3 séances dans une même semaine',
    check: (workouts) => {
      if (workouts.length < 3) return false;
      const sortedDates = workouts.map(w => new Date(w.date).getTime()).sort((a,b) => a-b);
      for (let i = 0; i <= sortedDates.length - 3; i++) {
        if (sortedDates[i+2] - sortedDates[i] <= 7 * 24 * 60 * 60 * 1000) return true;
      }
      return false;
    }
  },
  {
    id: 'perfect_week',
    name: 'Semaine Parfaite ⭐',
    description: 'A fait 5 séances dans une même semaine',
    check: (workouts) => {
      if (workouts.length < 5) return false;
      const sortedDates = workouts.map(w => new Date(w.date).getTime()).sort((a,b) => a-b);
      for (let i = 0; i <= sortedDates.length - 5; i++) {
        if (sortedDates[i+4] - sortedDates[i] <= 7 * 24 * 60 * 60 * 1000) return true;
      }
      return false;
    },
    secret: true
  },
  {
    id: 'consistency_month',
    name: 'La Machine 🤖',
    description: 'A fait 15 séances en 30 jours',
    check: (workouts) => {
      if (workouts.length < 15) return false;
      const sortedDates = workouts.map(w => new Date(w.date).getTime()).sort((a,b) => a-b);
      for (let i = 0; i <= sortedDates.length - 15; i++) {
        if (sortedDates[i+14] - sortedDates[i] <= 30 * 24 * 60 * 60 * 1000) return true;
      }
      return false;
    }
  },
  {
    id: 'no_rest',
    name: 'Sans Répit 🔥',
    description: "S'est entraîné 4 jours consécutifs",
    check: (workouts) => {
      if (workouts.length < 4) return false;
      const sortedDates = workouts.map(w => new Date(w.date).getTime()).sort((a,b) => a-b);
      for (let i = 0; i <= sortedDates.length - 4; i++) {
        if (sortedDates[i+3] - sortedDates[i] <= 4 * 24 * 60 * 60 * 1000) return true;
      }
      return false;
    },
    secret: true
  },
  {
    id: 'early_bird',
    name: 'Lève-tôt 🌅',
    description: "S'est entraîné avant 7h du matin",
    check: (workouts) => workouts.some(w => new Date(w.date).getHours() < 7),
    secret: true
  },
  {
    id: 'night_owl',
    name: 'Oiseau de Nuit 🦉',
    description: "S'est entraîné après 22h",
    check: (workouts) => workouts.some(w => new Date(w.date).getHours() >= 22)
  },
  {
    id: 'vampire',
    name: 'Vampire 🧛‍♂️',
    description: "S'est entraîné entre minuit et 4h du matin",
    check: (workouts) => workouts.some(w => new Date(w.date).getHours() >= 0 && new Date(w.date).getHours() < 4),
    secret: true
  },
  {
    id: 'weekend_warrior',
    name: 'Guerrier du Dimanche 🏖️',
    description: "S'est entraîné un dimanche",
    check: (workouts) => workouts.some(w => new Date(w.date).getDay() === 0)
  },
  {
    id: 'chest_day',
    name: 'International Chest Day 🌍',
    description: "A fait les pecs un lundi",
    check: (workouts) => workouts.some(w => new Date(w.date).getDay() === 1 && getWorkoutItems(w).some(ex => ex.name && (ex.name.toLowerCase().includes('développé') || ex.name.toLowerCase().includes('pec')))),
    secret: true
  },
  {
    id: 'squat_master',
    name: 'Maître du Squat 🦵',
    description: "A fait plus de 20 séries de squat au total",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutItems(w).forEach(ex => {
        if (ex.name && ex.name.toLowerCase().includes('squat')) {
          if (ex.sets) count += ex.sets.filter(s => !s.isWarmup).length;
        }
      }));
      return count >= 20;
    },
    secret: true
  },
  {
    id: 'bench_bro',
    name: 'Bro du Banc 🦍',
    description: "A fait plus de 20 séries de développé couché au total",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutItems(w).forEach(ex => {
        if (ex.name && (ex.name.toLowerCase().includes('développé couché') || ex.name.toLowerCase().includes('bench press'))) {
          if (ex.sets) count += ex.sets.filter(s => !s.isWarmup).length;
        }
      }));
      return count >= 20;
    },
    secret: true
  },
  {
    id: 'deadlift_lover',
    name: 'Démon du Soulevé 😈',
    description: "A fait plus de 20 séries de soulevé de terre au total",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutItems(w).forEach(ex => {
        if (ex.name && (ex.name.toLowerCase().includes('terre') || ex.name.toLowerCase().includes('deadlift'))) {
          if (ex.sets) count += ex.sets.filter(s => !s.isWarmup).length;
        }
      }));
      return count >= 20;
    },
    secret: true
  },
  {
    id: 'biceps_flex',
    name: 'Flex 💪',
    description: "A fait plus de 20 séries de curl au total",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutItems(w).forEach(ex => {
        if (ex.name && ex.name.toLowerCase().includes('curl')) {
          if (ex.sets) count += ex.sets.filter(s => !s.isWarmup).length;
        }
      }));
      return count >= 20;
    }
  },
  {
    id: 'volume_god',
    name: 'Dieu du Volume 📚',
    description: "A fait plus de 30 séries en une seule séance",
    check: (workouts) => workouts.some(w => getWorkoutSets(w).filter(s => !s.isWarmup).length >= 30),
    secret: true
  },
  {
    id: '1rm_tester',
    name: 'Go Muscu 🦍',
    description: "A tenté un PR (série de 1 répétition)",
    check: (workouts) => workouts.some(w => getWorkoutSets(w).some(s => !s.isWarmup && parseInt(s.reps) === 1))
  },
  {
    id: 'high_reps',
    name: 'L\'Endurant 🔋',
    description: "A fait une série de plus de 20 répétitions",
    check: (workouts) => workouts.some(w => getWorkoutSets(w).some(s => !s.isWarmup && parseInt(s.reps) >= 20))
  },
  {
    id: 'superset_master',
    name: 'Maître du Superset 🔄',
    description: "A réalisé un Superset lors d'une séance",
    check: (workouts) => workouts.some(w => w.exercises && w.exercises.some(ex => ex.type === 'superset'))
  },
  {
    id: 'drop_set_king',
    name: 'Roi du Drop Set 📉',
    description: "A utilisé la technique du Drop Set",
    check: (workouts) => workouts.some(w => getWorkoutSets(w).some(s => s.isDropSet))
  },
  {
    id: 'century_club',
    name: 'Club des 100 💯',
    description: "A fait une série de 100 répétitions ou plus",
    check: (workouts) => workouts.some(w => getWorkoutSets(w).some(s => !s.isWarmup && parseInt(s.reps) >= 100)),
    secret: true
  },
  {
    id: 'heavy_squat',
    name: 'Jambes d\'Acier 🦾',
    description: "A squatté 100 kg ou plus",
    check: (workouts) => workouts.some(w => getWorkoutItems(w).some(ex => ex.name && ex.name.toLowerCase().includes('squat') && ex.sets && ex.sets.some(s => parseFloat(s.weight) >= 100)))
  },
  {
    id: 'heavy_bench',
    name: 'Pecs en Béton 🧱',
    description: "A fait du développé couché à 100 kg ou plus",
    check: (workouts) => workouts.some(w => getWorkoutItems(w).some(ex => ex.name && (ex.name.toLowerCase().includes('développé couché') || ex.name.toLowerCase().includes('bench press')) && ex.sets && ex.sets.some(s => parseFloat(s.weight) >= 100)))
  },
  {
    id: 'heavy_deadlift',
    name: 'Dos de Gorille 🦍',
    description: "A soulevé de terre 150 kg ou plus",
    check: (workouts) => workouts.some(w => getWorkoutItems(w).some(ex => ex.name && (ex.name.toLowerCase().includes('terre') || ex.name.toLowerCase().includes('deadlift')) && ex.sets && ex.sets.some(s => parseFloat(s.weight) >= 150)))
  },
  {
    id: 'two_a_day',
    name: 'Double Dose 💊',
    description: "S'est entraîné deux fois le même jour",
    check: (workouts) => {
      const dates = workouts.map(w => new Date(w.date).toLocaleDateString());
      return new Set(dates).size !== dates.length;
    },
    secret: true
  },
  {
    id: 'full_body',
    name: 'Full Body 🧍‍♂️',
    description: "A travaillé au moins 4 groupes musculaires différents dans la même séance",
    check: (workouts) => workouts.some(w => w.muscles && w.muscles.length >= 4)
  },
  {
    id: 'spartan',
    name: 'Spartiate 🛡️',
    description: "A terminé 300 séances d'entraînement",
    check: (workouts) => workouts.length >= 300,
    secret: true
  },
  {
    id: 'zen_master',
    name: 'Maître Zen 🧘‍♂️',
    description: "A pris plus de 3 minutes de repos moyen sur une séance",
    check: (workouts) => workouts.some(w => w.restTime && parseFloat(w.restTime) >= 3.0)
  },
  {
    id: 'million_club',
    name: 'Club du Million 💸',
    description: 'A soulevé 1 million de kilos (1000 tonnes) au total',
    check: (workouts) => {
      let total = 0;
      workouts.forEach(w => { total += (w.totalVolume !== undefined ? w.totalVolume : calculateWorkoutVolume(w)); });
      return total >= 1000000;
    },
    secret: true
  },
  {
    id: 'squat_master_2',
    name: 'Tom Platz 🦵',
    description: "A fait 50 séries de squat au total",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutItems(w).forEach(ex => {
        if (ex.name && (ex.name.toLowerCase().includes('squat'))) {
          if (ex.sets) count += ex.sets.filter(s => !s.isWarmup).length;
        }
      }));
      return count >= 50;
    },
    secret: true
  },
  {
    id: 'bench_bro_2',
    name: "Pecs de l'Espace 🦍",
    description: "A fait 50 séries de développé couché au total",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutItems(w).forEach(ex => {
        if (ex.name && (ex.name.toLowerCase().includes('développé couché') || ex.name.toLowerCase().includes('bench press'))) {
          if (ex.sets) count += ex.sets.filter(s => !s.isWarmup).length;
        }
      }));
      return count >= 50;
    },
    secret: true
  },
  {
    id: 'deadlift_lover_2',
    name: 'Dos Argenté 🦧',
    description: "A fait 50 séries de soulevé de terre au total",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutItems(w).forEach(ex => {
        if (ex.name && (ex.name.toLowerCase().includes('terre') || ex.name.toLowerCase().includes('deadlift'))) {
          if (ex.sets) count += ex.sets.filter(s => !s.isWarmup).length;
        }
      }));
      return count >= 50;
    },
    secret: true
  },
  {
    id: 'biceps_flex_2',
    name: 'Gros Bras 💪',
    description: "A fait 50 séries de curl au total",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutItems(w).forEach(ex => {
        if (ex.name && ex.name.toLowerCase().includes('curl')) {
          if (ex.sets) count += ex.sets.filter(s => !s.isWarmup).length;
        }
      }));
      return count >= 50;
    }
  },
  {
    id: 'warmup_king',
    name: 'Jamais Froid 🌡️',
    description: "A enregistré plus de 100 séries d'échauffement au total",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutSets(w).forEach(s => { if (s.isWarmup) count++; }));
      return count >= 100;
    }
  },
  {
    id: 'unilateral_master',
    name: 'Équilibre Parfait ⚖️',
    description: "A effectué 50 séries en unilatéral",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutSets(w).forEach(s => { if (s.isUnilateral) count++; }));
      return count >= 50;
    }
  },
  {
    id: 'pullup_god',
    name: 'Antigravité 🦇',
    description: "A fait plus de 20 séries de tractions",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutItems(w).forEach(ex => {
        if (ex.name && (ex.name.toLowerCase().includes('traction') || ex.name.toLowerCase().includes('pull-up') || ex.name.toLowerCase().includes('pull up'))) {
          if (ex.sets) count += ex.sets.filter(s => !s.isWarmup).length;
        }
      }));
      return count >= 20;
    }
  },
  {
    id: 'bodyweight_ninja',
    name: 'Ninja au Poids de Corps 🥷',
    description: "A fait une séance uniquement au poids de corps (0 kg)",
    check: (workouts) => workouts.some(w => {
      const sets = getWorkoutSets(w).filter(s => !s.isWarmup);
      if (sets.length === 0) return false;
      return sets.every(s => s.isUnilateral ? (parseFloat(s.weightL) === 0 || !s.weightL) && (parseFloat(s.weightR) === 0 || !s.weightR) : (parseFloat(s.weight) === 0 || !s.weight));
    })
  },
  {
    id: 'fast_and_furious',
    name: 'Rapide & Furieux 🏎️',
    description: "A fait 15 séries en moins de 30 minutes",
    check: (workouts) => workouts.some(w => w.duration && parseInt(w.duration) > 0 && parseInt(w.duration) <= 30 && getWorkoutSets(w).filter(s => !s.isWarmup).length >= 15)
  },
  {
    id: 'late_night_grind',
    name: "L'Heure du Crime 🦇",
    description: "S'est entraîné un vendredi ou samedi soir après 23h",
    check: (workouts) => workouts.some(w => {
      const date = new Date(w.date);
      const day = date.getDay();
      const hour = date.getHours();
      return (day === 5 || day === 6) && (hour >= 23 || hour < 3);
    }),
    secret: true
  },
  {
    id: 'shoulder_boulders',
    name: 'Épaules 3D 🥥',
    description: "A fait plus de 30 séries d'épaules au total",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutItems(w).forEach(ex => {
        if (ex.muscles && ex.muscles.includes('Épaules') || (ex.muscle && ex.muscle.includes('Épaules'))) {
          if (ex.sets) count += ex.sets.filter(s => !s.isWarmup).length;
        }
      }));
      return count >= 30;
    }
  },
  {
    id: 'triceps_horseshoe',
    name: 'Fer à Cheval 🧲',
    description: "A fait plus de 30 séries de triceps au total",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutItems(w).forEach(ex => {
        if (ex.muscles && ex.muscles.includes('Triceps') || (ex.muscle && ex.muscle.includes('Triceps'))) {
          if (ex.sets) count += ex.sets.filter(s => !s.isWarmup).length;
        }
      }));
      return count >= 30;
    }
  },
  {
    id: 'ab_cruncher',
    name: 'Tablette de Chocolat 🍫',
    description: "A fait plus de 30 séries d'abdos au total",
    check: (workouts) => {
      let count = 0;
      workouts.forEach(w => getWorkoutItems(w).forEach(ex => {
        if (ex.muscles && ex.muscles.includes('Abdos') || (ex.muscle && ex.muscle.includes('Abdos'))) {
          if (ex.sets) count += ex.sets.filter(s => !s.isWarmup).length;
        }
      }));
      return count >= 30;
    }
  },
  {
    id: 'gym_rat',
    name: 'Rat de Salle 🐀',
    description: "A fait 20 séances en 30 jours",
    check: (workouts) => {
      if (workouts.length < 20) return false;
      const sortedDates = workouts.map(w => new Date(w.date).getTime()).sort((a,b) => a-b);
      for (let i = 0; i <= sortedDates.length - 20; i++) {
        if (sortedDates[i+19] - sortedDates[i] <= 30 * 24 * 60 * 60 * 1000) return true;
      }
      return false;
    }
  },
  {
    id: 'powerlifter',
    name: 'Force Athlétique 🏋️',
    description: "A fait du Squat, du Développé Couché et du Soulevé de Terre dans la même séance (SBD)",
    check: (workouts) => workouts.some(w => {
      const items = getWorkoutItems(w).map(e => e.name ? e.name.toLowerCase() : '');
      const hasSquat = items.some(n => n.includes('squat'));
      const hasBench = items.some(n => n.includes('développé couché') || n.includes('bench press'));
      const hasDeadlift = items.some(n => n.includes('terre') || n.includes('deadlift'));
      return hasSquat && hasBench && hasDeadlift;
    }),
    secret: true
  },
  {
    id: 'century_workout',
    name: 'Centurion 🛡️',
    description: "A fait 100 répétitions ou plus au total dans une seule séance",
    check: (workouts) => workouts.some(w => {
      let reps = 0;
      getWorkoutSets(w).forEach(s => { if (!s.isWarmup) reps += (parseInt(s.reps)||0) + (parseInt(s.repsL)||0) + (parseInt(s.repsR)||0); });
      return reps >= 100;
    })
  },
  {
    id: 'thousand_reps',
    name: 'Machine à Reps 🎰',
    description: "A fait plus de 1000 répétitions au total depuis le début",
    check: (workouts) => {
      let reps = 0;
      workouts.forEach(w => getWorkoutSets(w).forEach(s => { if (!s.isWarmup) reps += (parseInt(s.reps)||0) + (parseInt(s.repsL)||0) + (parseInt(s.repsR)||0); }));
      return reps >= 1000;
    }
  },
  {
    id: '1_year_anniversary',
    name: '1 An de Fonte 🎂',
    description: "S'est entraîné sur une période d'au moins 365 jours",
    check: (workouts) => {
      if (workouts.length < 2) return false;
      const sortedDates = workouts.map(w => new Date(w.date).getTime()).sort((a,b) => a-b);
      return sortedDates[sortedDates.length - 1] - sortedDates[0] >= 365 * 24 * 60 * 60 * 1000;
    }
  },
  {
    id: 'early_bird_2',
    name: 'Le Club des 5h 🕔',
    description: "S'est entraîné avant 6h du matin",
    check: (workouts) => workouts.some(w => new Date(w.date).getHours() < 6),
    secret: true
  },
  {
    id: 'never_skip_leg_day',
    name: 'Never Skip Leg Day 🍗',
    description: "A fait 2 séances ciblant les jambes dans la même semaine",
    check: (workouts) => {
      const legMuscles = ['Jambes', 'Quadriceps', 'Ischio-jambiers', 'Mollets', 'Fessier'];
      const legWorkouts = workouts.filter(w => {
        return getWorkoutItems(w).some(ex => {
          const mList = ex.muscles || (ex.muscle ? [ex.muscle] : []);
          return mList.some(m => legMuscles.includes(m));
        });
      });
      if (legWorkouts.length < 2) return false;
      const sortedDates = legWorkouts.map(w => new Date(w.date).getTime()).sort((a,b) => a-b);
      for (let i = 0; i <= sortedDates.length - 2; i++) {
        if (sortedDates[i+1] - sortedDates[i] <= 7 * 24 * 60 * 60 * 1000) return true;
      }
      return false;
    }
  },
  {
    id: 'perfect_month',
    name: 'Sans Faille 💎',
    description: "A fait 12 séances en 30 jours (3 séances / semaine)",
    check: (workouts) => {
      if (workouts.length < 12) return false;
      const sortedDates = workouts.map(w => new Date(w.date).getTime()).sort((a,b) => a-b);
      for (let i = 0; i <= sortedDates.length - 12; i++) {
        if (sortedDates[i+11] - sortedDates[i] <= 30 * 24 * 60 * 60 * 1000) return true;
      }
      return false;
    }
  },
  {
    id: 'target_locked',
    name: 'Cible Verrouillée 🎯',
    description: "A créé 10 objectifs",
    check: (workouts, goals = []) => goals.length >= 10
  },
  {
    id: 'first_victory',
    name: 'Première Victoire 🏆',
    description: "A atteint son premier objectif",
    check: (workouts, goals = []) => goals.filter(g => g.completed).length >= 1
  },
  {
    id: 'determined',
    name: 'Déterminé 😤',
    description: "A atteint 5 objectifs",
    check: (workouts, goals = []) => goals.filter(g => g.completed).length >= 5
  },
  {
    id: 'unstoppable',
    name: 'Inarrêtable 🚀',
    description: "A atteint 10 objectifs",
    check: (workouts, goals = []) => goals.filter(g => g.completed).length >= 10
  },
  {
    id: 'boss_slayer_1',
    name: 'Chasseur de Titans 🗡️',
    description: "A vaincu 1 World Boss",
    category: 'special',
    check: (workouts, goals = [], profile = null) => (profile?.bossKills || 0) >= 1
  },
  {
    id: 'boss_slayer_10',
    name: 'Bourreau des Colosses ⚔️',
    description: "A vaincu 10 World Boss",
    category: 'special',
    check: (workouts, goals = [], profile = null) => (profile?.bossKills || 0) >= 10
  },
  {
    id: 'boss_slayer_20',
    name: 'Fléau Divin 👑',
    description: "A vaincu 20 World Boss",
    category: 'special',
    check: (workouts, goals = [], profile = null) => (profile?.bossKills || 0) >= 20
  }
];

export const getEarnedBadges = (workouts = [], goals = [], profile = null) => {
  const profileBadgeIds = new Set(Array.isArray(profile?.badges) ? profile.badges : []);
  const workoutBadgeIds = new Set(
    (workouts || []).flatMap(w => (w?.unlockedBadges || []).map(b => (typeof b === 'object' ? b.id : b)).filter(Boolean))
  );

  return BADGES.filter(b => {
    if (profileBadgeIds.has(b.id) || workoutBadgeIds.has(b.id)) return true;
    return b.check(workouts, goals, profile);
  });
};

export const getLockedBadges = (workouts = [], goals = [], profile = null) => {
  const earned = getEarnedBadges(workouts, goals, profile);
  const earnedIds = new Set(earned.map(b => b.id));
  return BADGES.filter(b => !earnedIds.has(b.id));
};
